import { useEffect } from 'react';
import {
  fetchMarathiCandidates,
  getCandidateState,
  updateCandidateState,
  hideCandidates,
  commitCandidate,
} from '../services/marathiTransliterationService';

export const INPUT_LANGUAGE_STORAGE_KEY = 'gp-input-language';
export const INPUT_LANGUAGE_EVENT = 'gp-input-language-change';

export const getInputLanguage = () => {
  try {
    return window.localStorage.getItem(INPUT_LANGUAGE_STORAGE_KEY) === 'en' ? 'en' : 'mr';
  } catch {
    return 'mr';
  }
};

const EXCLUDED_INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'email',
  'file',
  'hidden',
  'month',
  'number',
  'password',
  'radio',
  'range',
  'tel',
  'time',
  'url',
  'week',
]);

const EXCLUDED_FIELD_PATTERN = /(^|[\s_-])(aadhaar|aadhar|amount|code|contact[_-]?phone|date|email|fee|file|id|image|latitude|link|login|longitude|mobile|number|password|phone|photo|pincode|pin|price|slug|tax|time|uid|url|username|user[_-]?name|website|year|zip)([\s_-]|$)/i;
const EXCLUDED_PLACEHOLDER_PATTERN = /(https?:\/\/|www\.|\.com|@|birth,\s*death|ration_card_help|example\.com|url|website|image|photo|license|gst)/i;

const isTextElement = element =>
  element instanceof HTMLTextAreaElement ||
  (element instanceof HTMLInputElement && !EXCLUDED_INPUT_TYPES.has((element.type || 'text').toLowerCase()));

const fieldIdentity = element => [
  element.name,
  element.id,
  element.autocomplete,
  element.getAttribute('aria-label'),
  element.getAttribute('placeholder'),
].filter(Boolean).join(' ');

export const shouldTransliterate = element => {
  if (!isTextElement(element) || element.disabled || element.readOnly) return false;
  if (element.dataset.marathiInput === 'true') return true;
  if (element.dataset.marathiInput === 'false') return false;
  // Strict English check: If selected input language is English, do not transliterate
  if (getInputLanguage() === 'en') return false;

  const identity = fieldIdentity(element);
  if (EXCLUDED_FIELD_PATTERN.test(identity)) return false;
  if (EXCLUDED_PLACEHOLDER_PATTERN.test(identity)) return false;
  return true;
};

const isWordChar = char => Boolean(char && /[A-Za-z0-9]/.test(char));

const getWordAtCursor = element => {
  const value = element.value || '';
  const cursor = element.selectionStart ?? value.length;
  if (cursor === 0) return null;

  let start = cursor;
  while (start > 0 && isWordChar(value[start - 1])) {
    start -= 1;
  }

  const word = value.slice(start, cursor);
  if (!word || !/[A-Za-z]/.test(word)) return null;

  return { word, start, end: cursor };
};

const getOverlayPosition = element => {
  const rect = element.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  let top = rect.bottom + 6;
  if (spaceBelow < 55 && rect.top > 55) {
    top = rect.top - 46;
  }
  const left = Math.max(12, Math.min(rect.left, window.innerWidth - 380));
  return { top, left };
};

let fetchSeq = 0;

export default function useMarathiInputTransliteration() {
  useEffect(() => {
    const handleKeyDown = event => {
      const element = event.target;
      if (!shouldTransliterate(element) || event.isComposing) {
        hideCandidates();
        return;
      }

      const currentState = getCandidateState();
      if (!currentState.visible || currentState.targetElement !== element) {
        return;
      }

      // Space key: commit currently selected candidate and add a space
      if (event.key === ' ' || event.code === 'Space') {
        event.preventDefault();
        commitCandidate(currentState.selectedIndex, true);
        return;
      }

      // Enter or Tab key: commit without space
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        commitCandidate(currentState.selectedIndex, false);
        return;
      }

      // Number keys 1-5 to select alternative candidates
      if (event.key >= '1' && event.key <= '5') {
        const candidateIndex = Number(event.key) - 1;
        if (candidateIndex < currentState.candidates.length) {
          event.preventDefault();
          commitCandidate(candidateIndex, false);
          return;
        }
      }

      // Navigation: ArrowDown / ArrowRight
      if (event.key === 'ArrowDown' || (event.altKey && event.key === 'ArrowRight')) {
        event.preventDefault();
        const nextIdx = (currentState.selectedIndex + 1) % currentState.candidates.length;
        updateCandidateState({ selectedIndex: nextIdx });
        return;
      }

      // Navigation: ArrowUp / ArrowLeft
      if (event.key === 'ArrowUp' || (event.altKey && event.key === 'ArrowLeft')) {
        event.preventDefault();
        const prevIdx =
          (currentState.selectedIndex - 1 + currentState.candidates.length) %
          currentState.candidates.length;
        updateCandidateState({ selectedIndex: prevIdx });
        return;
      }

      // Escape: dismiss candidate suggestions, keep English text
      if (event.key === 'Escape') {
        event.preventDefault();
        hideCandidates();
        return;
      }
    };

    const processInputSuggestions = async element => {
      if (!shouldTransliterate(element)) {
        hideCandidates();
        return;
      }

      const wordInfo = getWordAtCursor(element);
      if (!wordInfo) {
        hideCandidates();
        return;
      }

      const currentSeq = ++fetchSeq;
      const candidates = await fetchMarathiCandidates(wordInfo.word);

      if (currentSeq !== fetchSeq || !shouldTransliterate(element)) {
        return;
      }

      if (candidates && candidates.length > 0) {
        updateCandidateState({
          visible: true,
          word: wordInfo.word,
          candidates,
          selectedIndex: 0,
          targetElement: element,
          wordRange: { start: wordInfo.start, end: wordInfo.end },
          position: getOverlayPosition(element),
        });
      } else {
        hideCandidates();
      }
    };

    const handleInput = event => {
      const element = event.target;
      if (event.isComposing) return;
      processInputSuggestions(element);
    };

    const handleKeyUp = event => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        processInputSuggestions(event.target);
      }
    };

    const handleFocus = event => {
      if (!shouldTransliterate(event.target)) {
        hideCandidates();
      }
    };

    const handlePointerDown = event => {
      if (event.target.closest?.('.marathi-candidate-overlay')) {
        // Interacting with candidate bar
        return;
      }
      const currentState = getCandidateState();
      if (currentState.visible && event.target !== currentState.targetElement) {
        hideCandidates();
      }
    };

    const handleLanguageChange = () => {
      if (getInputLanguage() === 'en') {
        hideCandidates();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('input', handleInput, true);
    document.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('focusin', handleFocus, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener(INPUT_LANGUAGE_EVENT, handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('input', handleInput, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('focusin', handleFocus, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener(INPUT_LANGUAGE_EVENT, handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);
}
